/**
 * Calendar Event Type API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type { BulkResultData, BulkResultResponse } from '../types/bulk';
import type {
  CalendarEventType,
  CalendarEventTypeListResponse,
  CalendarEventTypeResponse,
  CalendarEventTypeCreateInput,
  CalendarEventTypeUpdateInput,
} from '../types/calendar-event-type';

interface ListCalendarEventTypesOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

class CalendarEventTypeService {
  /**
   * List calendar event types with pagination.
   */
  async listCalendarEventTypes(
    workspaceId: string,
    idToken: string,
    options: ListCalendarEventTypesOptions = {}
  ): Promise<CalendarEventTypeListResponse> {
    const { page = 1, size = 50, sortBy = 'name', order = 'asc', search } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<CalendarEventTypeListResponse>(`/calendar-event-types`, {
      params: { skip, limit: size, sort_by: sortBy, order, search },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list calendar event types');
    }

    return response.data;
  }

  /**
   * Get a single calendar event type by ID.
   */
  async getCalendarEventType(
    workspaceId: string,
    eventTypeId: string,
    idToken: string
  ): Promise<CalendarEventType> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<CalendarEventTypeResponse>(
      `/calendar-event-types/${eventTypeId}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get calendar event type');
    }

    return response.data.data;
  }

  /**
   * Create a new calendar event type.
   */
  async createCalendarEventType(
    workspaceId: string,
    idToken: string,
    data: CalendarEventTypeCreateInput
  ): Promise<CalendarEventType> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<CalendarEventTypeResponse>(`/calendar-event-types`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create calendar event type');
    }

    return response.data.data;
  }

  /**
   * Bulk create calendar event types; duplicates skipped silently.
   */
  async bulkCreateCalendarEventTypes(
    workspaceId: string,
    idToken: string,
    names: readonly string[]
  ): Promise<BulkResultData> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<BulkResultResponse>(
      `/calendar-event-types/bulk`,
      { names },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk create calendar event types');
    }

    return response.data.data;
  }

  /**
   * Update a calendar event type.
   */
  async updateCalendarEventType(
    workspaceId: string,
    eventTypeId: string,
    idToken: string,
    data: CalendarEventTypeUpdateInput
  ): Promise<CalendarEventType> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<CalendarEventTypeResponse>(
      `/calendar-event-types/${eventTypeId}`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update calendar event type');
    }

    return response.data.data;
  }

  /**
   * Delete a calendar event type.
   */
  async deleteCalendarEventType(
    workspaceId: string,
    eventTypeId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/calendar-event-types/${eventTypeId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const calendarEventTypeService = new CalendarEventTypeService();
export default calendarEventTypeService;
